import React, { useEffect, useState } from 'react';
import { ArrowSquareOut, Buildings, CheckCircle, CircleNotch, FolderSimple, Sparkle, X } from '@phosphor-icons/react';
import { Chip } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { useNotification } from '../../../contexts/NotificationContext';
import {
  AblageVorschlag, ladeOrdnerpfade, legeAnhangAb, markiereVorschlag, driveDateiUrl,
} from '../../../lib/dokumente/ablageApi';

export interface AnhangRef {
  id: string;
  dateiname: string;
  dateigroesse: number | null;
  dateityp: string | null;
  graph_message_id: string | null;
  graph_attachment_id: string | null;
}

interface AblageKarteProps {
  anhang: AnhangRef;
  vorschlag: AblageVorschlag;
  emailObjektId: string | null;
  userId: string | null;
  onErledigt: () => void;
  /** Mobil: 44-px-Ziele. */
  mobil?: boolean;
}

interface ObjektTreffer { id: string; objektnummer: string | null; bezeichnung: string | null; strasse: string | null; stadt: string | null }

function objektLabel(o: ObjektTreffer): string {
  const teile = [o.objektnummer, o.bezeichnung || [o.strasse, o.stadt].filter(Boolean).join(', ')].filter(Boolean);
  return teile.join(' – ') || o.id;
}

export default function AblageKarte({ anhang, vorschlag, emailObjektId, userId, onErledigt, mobil = false }: AblageKarteProps) {
  const { showToast } = useNotification();

  const [objektId, setObjektId] = useState<string | null>(vorschlag.objekt_id || emailObjektId);
  const [objektText, setObjektText] = useState('');
  const [objektTreffer, setObjektTreffer] = useState<ObjektTreffer[]>([]);
  const [objektOffen, setObjektOffen] = useState(false);
  const [zielPfad, setZielPfad] = useState(vorschlag.ziel_pfad || '');
  const [dateiname, setDateiname] = useState(vorschlag.dateiname);
  const [ordnerVorschlaege, setOrdnerVorschlaege] = useState<string[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  // Lokaler Status-Override: sobald die Datei in Drive liegt, ist die Karte SOFORT + dauerhaft
  // "abgelegt" — unabhaengig davon, ob der DB-Statusvermerk gelingt (verhindert Doppel-Ablage).
  const [lokal, setLokal] = useState<{ status: AblageVorschlag['status']; dokumentId?: string | null; gdriveFileId?: string | null; dateiname?: string; zielPfad?: string } | null>(null);
  const status = lokal?.status ?? vorschlag.status;

  // Aktuelles Objekt-Label anzeigen (aus objektId)
  useEffect(() => {
    let aktiv = true;
    if (!objektId) { setObjektText(''); return; }
    supabase.from('objekte').select('id, objektnummer, bezeichnung, strasse, stadt').eq('id', objektId).maybeSingle()
      .then(({ data }) => { if (aktiv && data) setObjektText(objektLabel(data as ObjektTreffer)); });
    return () => { aktiv = false; };
  }, [objektId]);

  // Ordner-Vorschläge fürs Objekt laden (Autocomplete)
  useEffect(() => {
    let aktiv = true;
    if (!objektId) { setOrdnerVorschlaege([]); return; }
    ladeOrdnerpfade(objektId).then((p) => { if (aktiv) setOrdnerVorschlaege(p); });
    return () => { aktiv = false; };
  }, [objektId]);

  async function sucheObjekt(q: string) {
    setObjektText(q);
    setObjektOffen(true);
    if (q.trim().length < 2) { setObjektTreffer([]); return; }
    const { data } = await supabase.from('objekte')
      .select('id, objektnummer, bezeichnung, strasse, stadt')
      .or(`objektnummer.ilike.%${q}%,bezeichnung.ilike.%${q}%,strasse.ilike.%${q}%`)
      .limit(8);
    setObjektTreffer((data || []) as ObjektTreffer[]);
  }

  function waehleObjekt(o: ObjektTreffer) {
    setObjektId(o.id);
    setObjektText(objektLabel(o));
    setObjektOffen(false);
    setObjektTreffer([]);
  }

  const kannAblegen = !!objektId && !!zielPfad.trim() && !!dateiname.trim() && !laedt
    && !!anhang.graph_message_id && !!anhang.graph_attachment_id;

  async function ablegen() {
    if (!objektId) return;
    setLaedt(true); setFehler(null);
    const nameJetzt = dateiname.trim();
    const pfadJetzt = zielPfad.trim();
    let r: { gdrive_file_id: string; dokument_id: string; ordner_id: string };
    try {
      r = await legeAnhangAb({ anhangId: anhang.id, objektId, zielPfad: pfadJetzt, dateiname: nameJetzt });
    } catch (e) {
      const msg = (e as Error).message || 'Ablage fehlgeschlagen';
      // Backend-Safeguard: Anhang ist bereits abgelegt -> Karte auf "abgelegt" setzen statt Fehler.
      if (/uq_dokumente_quelle_anhang|duplicate key/i.test(msg)) {
        setLokal({ status: 'abgelegt', dateiname: nameJetzt, zielPfad: pfadJetzt });
        showToast({ type: 'info', message: 'Dieser Anhang war bereits abgelegt.', modul: 'dokumente' });
        onErledigt();
        setLaedt(false);
        return;
      }
      setFehler(msg);
      showToast({ type: 'error', message: `Ablage fehlgeschlagen: ${msg}`, modul: 'dokumente' });
      setLaedt(false);
      return;
    }
    // Datei liegt in Drive -> Karte SOFORT + dauerhaft auf "abgelegt" (kein Doppel-Ablegen mehr moeglich).
    setLokal({ status: 'abgelegt', dokumentId: r.dokument_id, gdriveFileId: r.gdrive_file_id, dateiname: nameJetzt, zielPfad: pfadJetzt });
    showToast({ type: 'success', message: `Abgelegt: ${nameJetzt}`, modul: 'dokumente' });
    try {
      await markiereVorschlag(vorschlag.id, { status: 'abgelegt', dokument_id: r.dokument_id, gdrive_file_id: r.gdrive_file_id, entschieden_von: userId });
    } catch {
      // Datei liegt bereits; nur der Status-Vermerk fehlte -> weicher Hinweis, Karte bleibt abgelegt.
      showToast({ type: 'warning', message: 'Abgelegt, aber der Status konnte nicht gespeichert werden.', modul: 'dokumente' });
    }
    onErledigt();
    setLaedt(false);
  }

  async function ablehnen() {
    setLokal({ status: 'abgelehnt' });
    try { await markiereVorschlag(vorschlag.id, { status: 'abgelehnt', entschieden_von: userId }); } catch { /* best-effort */ }
    onErledigt();
  }

  async function wiederOeffnen() {
    setLokal({ status: 'offen' });
    try { await markiereVorschlag(vorschlag.id, { status: 'offen', entschieden_von: userId }); } catch { /* best-effort */ }
    onErledigt();
  }

  const karte: React.CSSProperties = { marginTop: 8, borderRadius: 16, padding: 12, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' };
  const feld: React.CSSProperties = { width: '100%', height: 44, padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: mobil ? 16 : 13 };
  const feldLabel: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--ink3)', marginBottom: 4 };
  const kh = mobil ? 44 : 36;

  // Abgelegt
  if (status === 'abgelegt') {
    const anzeigeName = lokal?.dateiname ?? vorschlag.dateiname;
    const anzeigePfad = lokal?.zielPfad ?? vorschlag.ziel_pfad;
    const fileId = lokal?.gdriveFileId ?? vorschlag.gdrive_file_id;
    return (
      <div className="flex items-center justify-between" style={{ ...karte, gap: 8 }}>
        <div className="flex items-center min-w-0" style={{ gap: 8 }}>
          <CheckCircle size={16} weight="fill" color="var(--ok)" className="shrink-0" />
          <div className="min-w-0">
            <div className="truncate" style={{ fontSize: 12, fontWeight: 700 }}>{anzeigeName}</div>
            <div className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{anzeigePfad}</div>
          </div>
        </div>
        {fileId && (
          <a href={driveDateiUrl(fileId)} target="_blank" rel="noreferrer" className="flex items-center shrink-0"
            style={{ height: kh, padding: '0 10px', gap: 5, borderRadius: 10, fontSize: 12, fontWeight: 700, background: 'var(--fill2)', color: 'var(--ink)', textDecoration: 'none' }}>
            <ArrowSquareOut size={13} weight="bold" /> Drive
          </a>
        )}
      </div>
    );
  }

  // Abgelehnt
  if (status === 'abgelehnt') {
    return (
      <div className="flex items-center justify-between" style={{ ...karte, gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Ablage abgelehnt</span>
        <button type="button" onClick={wiederOeffnen} disabled={laedt} className="cursor-pointer"
          style={{ height: kh, padding: '0 10px', borderRadius: 10, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, opacity: laedt ? 0.5 : 1 }}>Rückgängig</button>
      </div>
    );
  }

  // Offen / freigegeben / fehler → Formular
  return (
    <div className="flex flex-col" style={{ ...karte, gap: 10 }}>
      <div className="flex items-center flex-wrap" style={{ gap: 6, fontSize: 12, fontWeight: 800 }}>
        <FolderSimple size={15} weight="fill" color="var(--acc)" />
        In Google Drive ablegen
        <span className="flex-1" />
        {vorschlag.confidence >= 0.9 ? (
          <Chip ton="ok" groesse="xs">Regel-Treffer</Chip>
        ) : vorschlag.confidence >= 0.4 ? (
          <Chip ton="acc" groesse="xs" icon={Sparkle}>KI-Vorschlag</Chip>
        ) : null}
      </div>

      {/* Objekt */}
      <div className="relative">
        <label style={feldLabel}>Zielobjekt</label>
        <div className="relative">
          <Buildings size={14} color="var(--ink3)" className="absolute" style={{ left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            value={objektText}
            onChange={(e) => sucheObjekt(e.target.value)}
            onFocus={() => setObjektOffen(true)}
            placeholder="Objekt suchen…"
            style={{ ...feld, paddingLeft: 32 }}
          />
        </div>
        {objektOffen && objektTreffer.length > 0 && (
          <div className="absolute w-full flex flex-col overflow-auto" style={{ zIndex: 20, marginTop: 4, maxHeight: 200, borderRadius: 14, padding: 4, background: 'var(--bg)', boxShadow: '0 20px 40px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
            {objektTreffer.map((o) => (
              <button key={o.id} type="button" onClick={() => waehleObjekt(o)} className="immo-hover-flaeche flex items-center w-full text-left cursor-pointer"
                style={{ gap: 8, minHeight: mobil ? 44 : 34, padding: '0 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13 }}>
                {o.objektnummer && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{o.objektnummer}</span>}
                <span className="truncate">{o.bezeichnung || [o.strasse, o.stadt].filter(Boolean).join(', ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Ordner */}
      <div>
        <label style={feldLabel}>Zielordner</label>
        <input
          list={`ordner-${anhang.id}`}
          value={zielPfad}
          onChange={(e) => setZielPfad(e.target.value)}
          placeholder="z. B. 02 Finanzen/Rechnungen/2026/Eingangsrechnungen"
          style={feld}
        />
        <datalist id={`ordner-${anhang.id}`}>
          {ordnerVorschlaege.map((p) => <option key={p} value={p} />)}
        </datalist>
      </div>

      {/* Dateiname */}
      <div>
        <label style={feldLabel}>Dateiname</label>
        <input value={dateiname} onChange={(e) => setDateiname(e.target.value)} style={feld} />
      </div>

      {fehler && <p style={{ margin: 0, fontSize: 12, color: 'var(--bad)' }}>{fehler}</p>}
      {(!anhang.graph_message_id || !anhang.graph_attachment_id) && (
        <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--warn)' }}>Keine Postfach-Referenz — Anhang neu synchronisieren.</p>
      )}

      <div className="flex items-center justify-end" style={{ gap: 6 }}>
        <button type="button" onClick={ablehnen} disabled={laedt} className="flex items-center cursor-pointer"
          style={{ height: kh, padding: '0 14px', gap: 6, borderRadius: kh / 2, border: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700, opacity: laedt ? 0.5 : 1 }}>
          <X size={13} weight="bold" /> Ablehnen
        </button>
        <button type="button" onClick={ablegen} disabled={!kannAblegen} className="flex items-center cursor-pointer"
          style={{ height: kh, padding: '0 16px', gap: 6, borderRadius: kh / 2, border: 'none', background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)', fontSize: 13, fontWeight: 800, opacity: kannAblegen ? 1 : 0.5 }}>
          {laedt ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
          Ablegen
        </button>
      </div>
    </div>
  );
}
